// @ai-generated 2026-08-28T07:59:23+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
import { type ApiDynamicExportRun, EnumDynamicExportRunState } from '@Model';
import { renderDateTime, type StatusTimelineStep } from '@synqo/frontend-component-library';

const SECOND_IN_MS = 1000;
const MINUTE_IN_MS = 60 * SECOND_IN_MS;
const HOUR_IN_MS = 60 * MINUTE_IN_MS;

/**
 * How far the run got, as the three stages it moves through. A run is asynchronous, so "it failed"
 * is only half the answer — the other half is whether it ever started, which is the difference
 * between a broken script and a queue that never picked the job up.
 *
 * Only the two timestamps the run actually carries are used: it was scheduled at `created_at` and
 * last changed at `modified_at`. Nothing records when the pipeline started, so the middle stage
 * stays undated rather than claiming a beginning it does not know.
 */
export function buildExportRunTimeline(run: ApiDynamicExportRun): StatusTimelineStep[] {
    const isFailed = run.state === EnumDynamicExportRunState.Failed;
    const hasStarted = run.state !== EnumDynamicExportRunState.Scheduled;
    const hasEnded = isFailed || run.state === EnumDynamicExportRunState.Finished;

    return [
        {
            key: 'scheduled',
            title: 'Eingeplant',
            subtitle: renderDateTime(run.created_at, 'datetime'),
            state: hasStarted ? 'done' : 'current',
            note: hasStarted ? undefined : 'Der Export wartet auf einen freien Verarbeitungsplatz.',
        },
        {
            key: 'running',
            title: 'Verarbeitung',
            subtitle: runningSubtitle(run, hasStarted, hasEnded),
            state: runningState(hasStarted, hasEnded),
            accent: 'info',
            note: hasStarted && !hasEnded ? 'Der Export wird gerade ausgeführt.' : undefined,
        },
        isFailed ? failedStep(run) : finishedStep(run, hasEnded),
    ];
}

function runningState(hasStarted: boolean, hasEnded: boolean): StatusTimelineStep['state'] {
    if (!hasStarted) {
        return 'pending';
    }
    return hasEnded ? 'done' : 'current';
}

function runningSubtitle(run: ApiDynamicExportRun, hasStarted: boolean, hasEnded: boolean): string | undefined {
    if (!hasStarted) {
        return 'ausstehend';
    }
    return hasEnded ? undefined : `seit ${renderDateTime(run.modified_at, 'datetime')}`;
}

function failedStep(run: ApiDynamicExportRun): StatusTimelineStep {
    return {
        key: 'failed',
        title: 'Fehlgeschlagen',
        subtitle: outcomeSubtitle(run),
        state: 'failed',
    };
}

function finishedStep(run: ApiDynamicExportRun, hasEnded: boolean): StatusTimelineStep {
    return {
        key: 'finished',
        title: 'Abgeschlossen',
        subtitle: hasEnded ? outcomeSubtitle(run) : 'ausstehend',
        state: hasEnded ? 'done' : 'pending',
        note: hasEnded && !run.result_file_id ? 'Der Export hat keine Datei erzeugt.' : undefined,
    };
}

/** When the run ended and how long it took to get there. */
function outcomeSubtitle(run: ApiDynamicExportRun): string {
    const ended = renderDateTime(run.modified_at, 'datetime');
    const elapsed = formatElapsed(run.created_at, run.modified_at);
    return elapsed ? `${ended} · nach ${elapsed}` : ended;
}

/**
 * The span between two timestamps in the coarsest unit that still says something, or nothing when
 * either timestamp is missing or they are the same — a duration of "0 s" only adds noise.
 */
export function formatElapsed(from: string | undefined, to: string | undefined): string | undefined {
    if (!from || !to) {
        return undefined;
    }

    const elapsed = new Date(to).getTime() - new Date(from).getTime();
    if (Number.isNaN(elapsed) || elapsed < SECOND_IN_MS) {
        return undefined;
    }
    if (elapsed < MINUTE_IN_MS) {
        return `${Math.round(elapsed / SECOND_IN_MS)} s`;
    }
    if (elapsed < HOUR_IN_MS) {
        return `${Math.round(elapsed / MINUTE_IN_MS)} min`;
    }
    return `${Math.round(elapsed / HOUR_IN_MS)} h`;
}
